// Board card facts (WP-CARD-UX): who is working (agent profile, role, effective model of the
// current attempt), live elapsed times and a compact priority score chip. Timers share ONE
// 1-second clock for the whole board (lib/ticker.ts), paused while the tab is hidden; their
// text is not announced (aria-live off) and uses tabular numbers so it does not jitter.
import type { TaskCard } from "@maestros/contracts";
import { AGENT_ROLES, TERMINAL_TASK_STATUSES, WORKING_TASK_STATUSES } from "@maestros/contracts/enums";
import { useI18n } from "../../i18n/index.tsx";
import type { MessageKey } from "../../i18n/en.ts";
import { elapsedSince, formatCompactDuration, formatScore } from "../../lib/duration.ts";
import { statusView } from "../../lib/status.ts";
import { useTickingNow } from "../../lib/ticker.ts";
import { activeAttempt } from "../../lib/taskExecution.ts";
import { ClockIcon, CpuIcon } from "../../components/icons.tsx";

type Attempt = NonNullable<TaskCard["current_attempt"]>;

export function isWorkingStatus(status: string): boolean {
  return (WORKING_TASK_STATUSES as readonly string[]).includes(status);
}

function roleLabel(role: string, t: (k: MessageKey) => string): string {
  return (AGENT_ROLES as readonly string[]).includes(role) ? t(`agentRole.${role}` as MessageKey) : role;
}

/** Agent profile + role and the model actually answering (router alias shown as "via …"). */
export function CardAgent({ attempt }: { attempt: Attempt }) {
  const { t } = useI18n();
  const role = roleLabel(attempt.role, t);
  const effective = attempt.effective_model;
  const requested = attempt.requested_model;
  const model = effective ?? requested;
  return (
    <div className="task-card-agent" data-testid="card-agent">
      <p className="task-card-agent-line">
        <CpuIcon size={12} aria-hidden="true" />
        <span className="sr-only">{t("kanban.card.agent")}: </span>
        <span className="task-card-agent-name" data-testid="card-agent-name">
          {attempt.agent_profile_name ?? role}
        </span>
        <span className="subtle">
          {" · "}
          {t("kanban.card.attemptOf", { number: attempt.number, role })}
        </span>
      </p>
      {model ? (
        <p className="task-card-model" title={requested && effective && requested !== effective ? `${effective} (${t("kanban.card.viaRoute", { model: requested })})` : model}>
          <span className="sr-only">{t("kanban.card.model")}: </span>
          <span className="mono task-card-model-name" data-testid="card-model">
            {model}
          </span>
          {effective && requested && requested !== effective ? (
            <span className="subtle" data-testid="card-model-route">
              {" "}
              {t("kanban.card.viaRoute", { model: requested })}
            </span>
          ) : null}
          {!effective ? (
            <span className="subtle" data-testid="card-model-pending">
              {" · "}
              {t("kanban.card.modelPending")}
            </span>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}

/** Missing attempt history cannot prove that an agent has never run. */
export function CardExecution({ task }: { task: TaskCard }) {
  const { t } = useI18n();
  const attempt = activeAttempt(task);
  if (attempt) return <CardAgent attempt={attempt} />;
  if (!isWorkingStatus(task.status)) return null;
  return (
    <p className="subtle" data-testid="card-no-agent">
      {t(task.current_attempt === undefined ? "kanban.card.executionUnknown" : "kanban.card.noActiveAttempt")}
    </p>
  );
}

/** Attempt elapsed time is not measured effort or time spent waiting for a resource. */
export function CardTimers({ task }: { task: TaskCard }) {
  const { t } = useI18n();
  const now = useTickingNow();
  const attempt = activeAttempt(task);
  const working = attempt ? formatCompactDuration(elapsedSince(attempt.started_at, now) ?? Number.NaN) : null;
  const finished = (TERMINAL_TASK_STATUSES as readonly string[]).includes(task.status);
  const inStatus = formatCompactDuration(elapsedSince(task.status_since, now) ?? Number.NaN, { units: finished ? 1 : 2 });
  if (!working && !inStatus) return null;
  return (
    <p className="task-card-time" aria-live="off">
      <ClockIcon size={12} aria-hidden="true" />
      {working && attempt ? (
        <span data-testid="card-working-for" className="task-card-timer">
          {t(attempt.state === "waiting_resource" ? "kanban.card.waitingFor" : "kanban.card.workingFor", { duration: working })}
        </span>
      ) : null}
      {working && inStatus ? <span aria-hidden="true">·</span> : null}
      {inStatus ? (
        <span data-testid={finished ? "card-finished-ago" : "card-in-column"} className="task-card-timer subtle">
          {finished ? t("kanban.card.finishedAgo", { duration: inStatus }) : t("kanban.card.inColumnFor", { column: t(statusView(task.status).label), duration: inStatus })}
        </span>
      ) : null}
    </p>
  );
}

/** "score 0.250" chip; the full breakdown stays one keyboard/tap away (native disclosure). */
export function ScoreChip({ score, reason }: { score: number; reason: string | null }) {
  const { t } = useI18n();
  const label = formatScore(score);
  if (!reason) {
    return (
      <span className="badge score-chip-static" data-testid="score-chip">
        {t("kanban.card.score", { score: label })}
      </span>
    );
  }
  return (
    <details className="score-chip" data-testid="score-chip">
      <summary className="badge" aria-label={t("kanban.card.scoreDetails", { score: label })} title={reason}>
        {t("kanban.card.score", { score: label })}
      </summary>
      <p className="score-chip-reason mono" data-testid="score-reason">
        {reason}
      </p>
    </details>
  );
}
